import { Link, useLoaderData } from 'react-router';

import { Badge, Card, Icon, KpiCard } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, EmptyState } from '@ds/feedback';
import { PageHeader } from '@/comuns/PageHeader';
import { dados } from '@/data/porta';
import type { LinhaDeConsumoDeIa, OrcamentoDeIa } from '@/data/tipos';
import { TetosDeIa } from './TetosDeIa';

export interface CargaDoConsumoDeIa {
    linhas: LinhaDeConsumoDeIa[];
    orcamentos: OrcamentoDeIa[];
}

export async function carregarConsumoDeIa(): Promise<CargaDoConsumoDeIa> {
    const [linhas, orcamentos] = await Promise.all([
        dados().consumoDeIa(30),
        dados().orcamentosDeIa(),
    ]);
    return { linhas, orcamentos };
}

const numero = (v: number) => v.toLocaleString('pt-BR');

function quando(iso: string): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' });
}

/**
 * O consumo de IA: agentes, fluxos e base de conhecimento.
 *
 * Números, e só: o texto das perguntas não é guardado. Pergunta de clínica é
 * dado da clínica, e trazê-la para cá daria à plataforma o que a impersonação
 * existe justamente para restringir.
 */
export function ConsumoIa() {
    const { linhas, orcamentos } = useLoaderData() as CargaDoConsumoDeIa;

    const perguntas = linhas.reduce((s, l) => s + l.perguntas, 0);
    const tokens = linhas.reduce((s, l) => s + l.tokensEntrada + l.tokensSaida, 0);
    const erros = linhas.reduce((s, l) => s + l.erros, 0);
    const clinicas = new Set(linhas.map((l) => l.clinica)).size;

    /* A tabela casa linha e chave por um campo; a linha do consumo é agrupada
       por clínica e modelo, e é esse par que a identifica. */
    const comChave = linhas.map((l) => ({ ...l, id: `${l.clinica}-${l.modelo}` }));

    const colunas: DataTableColumn<LinhaDeConsumoDeIa & { id: string }>[] = [
        {
            key: 'clinica', header: 'Clínica',
            render: (l) => (l.clinicaId
                ? (
                    <Link to={`/admin/clinicas/${l.clinicaId}`} style={{ color: 'var(--text-primary)', fontWeight: 600, textDecoration: 'none' }}>
                        {l.clinica}
                    </Link>
                )
                /* Pergunta de quem opera a plataforma não é de clínica
                   nenhuma — e some se for atribuída a uma. */
                : <span style={{ color: 'var(--text-tertiary)' }}>{l.clinica}</span>),
        },
        {
            key: 'modelo', header: 'Modelo',
            render: (l) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{l.modelo}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{l.provedor}</span>
                </span>
            ),
        },
        { key: 'perguntas', header: 'Perguntas', align: 'right', width: 110, render: (l) => numero(l.perguntas) },
        {
            key: 'tokensEntrada', header: 'Tokens', align: 'right', width: 190,
            render: (l) => `${numero(l.tokensEntrada)} entrada · ${numero(l.tokensSaida)} saída`,
        },
        {
            key: 'erros', header: 'Erros', align: 'right', width: 100,
            render: (l) => (l.erros ? <Badge tone="danger">{l.erros}</Badge> : <span style={{ color: 'var(--text-tertiary)' }}>0</span>),
        },
        { key: 'ultimaEm', header: 'Última', align: 'right', width: 160, render: (l) => quando(l.ultimaEm) },
    ];

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Consumo de IA"
                description="O que a IA gastou nos últimos 30 dias, por clínica e por modelo."
            />

            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                <KpiCard label="Perguntas" value={numero(perguntas)} hint="nos últimos 30 dias" icon={<Icon name="sparkles" size={16} />} />
                <KpiCard label="Tokens" value={numero(tokens)} hint="entrada e saída somadas" icon={<Icon name="hash" size={16} />} />
                <KpiCard label="Clínicas usando" value={String(clinicas)} hint="com ao menos uma pergunta" icon={<Icon name="building-2" size={16} />} />
                <KpiCard
                    label="Erros" value={numero(erros)}
                    hint={erros ? 'revisar a chave do provedor' : 'nenhuma falha'}
                    deltaTone={erros ? 'down' : 'up'} icon={<Icon name="triangle-alert" size={16} />}
                />
            </div>

            {/* O que NÃO está aqui, dito antes que alguém procure. */}
            <Alert tone="info" dismissible={false} title="Tokens, não conversas">
                O texto das perguntas e das respostas não é guardado — o que fica é quantas foram, de qual
                clínica e em que modelo. Custo em reais depende da tabela de preço de cada provedor, que muda
                sem aviso e não é importada: converter tokens em dinheiro aqui daria um número que envelhece
                sozinho.
            </Alert>

            <TetosDeIa orcamentos={orcamentos} />

            <Card padding={0}>
                {linhas.length === 0 ? (
                    <EmptyState
                        icon="sparkles"
                        title="Nenhuma pergunta ainda"
                        description="O consumo aparece quando as clínicas começarem a usar os agentes, os fluxos ou a base de conhecimento. Sem chave configurada em Integrações e IA, nada disso responde."
                    />
                ) : (
                    <DataTable
                        columns={colunas} rows={comChave} rowKey="id"
                        stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    />
                )}
            </Card>
        </div>
    );
}
